import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { cartService } from '../services/api';
import { toast } from 'react-toastify';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const loadCart = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await cartService.getCart();
      setCart(response.data.cart);
    } catch (error) {
      console.error('Error loading cart:', error);
      setCart({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load cart when user changes
  useEffect(() => {
    if (user) {
      loadCart();
    } else {
      // Load cart from localStorage for guests
      loadGuestCart();
    }
  }, [user, loadCart]);

  const loadGuestCart = () => {
    try {
      const savedCart = localStorage.getItem('guestCart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      } else {
        setCart({ items: [], total: 0 });
      }
    } catch (error) {
      console.error('Error loading guest cart:', error);
      setCart({ items: [], total: 0 });
    }
  };

  const saveGuestCart = (cartData) => {
    try {
      localStorage.setItem('guestCart', JSON.stringify(cartData));
    } catch (error) {
      console.error('Error saving guest cart:', error);
    }
  };

  const addToCart = async (productId, quantity = 1, customizations = {}) => {
    setLoading(true);
    try {
      if (user) {
        const response = await cartService.addToCart({
          productId,
          quantity,
          customizations
        });
        setCart(response.data.cart);
        toast.success('Item added to cart');
      } else {
        // Handle guest cart
        const newCart = { ...cart };
        const existingItemIndex = newCart.items.findIndex(item => 
          item.productId === productId && 
          JSON.stringify(item.customizations) === JSON.stringify(customizations)
        );

        if (existingItemIndex > -1) {
          newCart.items[existingItemIndex].quantity += quantity;
        } else {
          // You'll need to fetch product details here for guest cart
          newCart.items.push({
            productId,
            quantity,
            customizations,
            // Add other product details
          });
        }

        // Recalculate total
        newCart.total = newCart.items.reduce((total, item) => {
          return total + (item.price * item.quantity);
        }, 0);

        setCart(newCart);
        saveGuestCart(newCart);
        toast.success('Item added to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (productId, quantity, customizations = {}) => {
    setLoading(true);
    try {
      if (user) {
        const response = await cartService.updateCart({
          productId,
          quantity,
          customizations
        });
        setCart(response.data.cart);
      } else {
        // Handle guest cart update
        const newCart = { ...cart };
        const itemIndex = newCart.items.findIndex(item => 
          item.productId === productId && 
          JSON.stringify(item.customizations) === JSON.stringify(customizations)
        );

        if (itemIndex !== -1) {
          if (quantity <= 0) {
            newCart.items.splice(itemIndex, 1);
          } else {
            newCart.items[itemIndex].quantity = quantity;
          }

          // Recalculate total
          newCart.total = newCart.items.reduce((total, item) => {
            return total + (item.price * item.quantity);
          }, 0);

          setCart(newCart);
          saveGuestCart(newCart);
        }
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      toast.error('Failed to update cart');
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId, customizations = {}) => {
    setLoading(true);
    try {
      if (user) {
        const response = await cartService.removeFromCart(productId, customizations);
        setCart(response.data.cart);
        toast.success('Item removed from cart');
      } else {
        // Handle guest cart removal
        const newCart = { ...cart };
        newCart.items = newCart.items.filter(item => !(
          item.productId === productId && 
          JSON.stringify(item.customizations) === JSON.stringify(customizations)
        ));

        // Recalculate total
        newCart.total = newCart.items.reduce((total, item) => {
          return total + (item.price * item.quantity);
        }, 0);

        setCart(newCart);
        saveGuestCart(newCart);
        toast.success('Item removed from cart');
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Failed to remove item from cart');
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      // Save snapshot before clearing in case we need to restore on uncertain payment status
      try {
        const snapshot = {
          items: cart.items,
          total: cart.total,
          savedAt: Date.now()
        };
        sessionStorage.setItem('preClearCartSnapshot', JSON.stringify(snapshot));
      } catch (e) {
        console.warn('Failed to store cart snapshot', e);
      }
      if (user) {
        const response = await cartService.clearCart();
        setCart(response.data.cart);
      } else {
        setCart({ items: [], total: 0 });
        localStorage.removeItem('guestCart');
      }
      // Always reload cart to update all UI (e.g., navbar)
      await loadCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCartItemCount = () => {
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  };

  const restoreCart = async (cartData) => {
    // Idempotent restore: sets quantities to the provided snapshot, not additive
    try {
      if (!cartData || !Array.isArray(cartData.items)) return;
      setLoading(true);
      if (user) {
        // Load current cart to decide whether to add or update
        let current = cart;
        // If cart likely stale (no items but snapshot has), fetch from backend
        if (!current.items || (current.items.length === 0 && cartData.items.length > 0)) {
          try {
            const resp = await cartService.getCart();
            current = resp.data.cart || { items: [], total: 0 };
          } catch (e) {
            console.warn('Failed to fetch cart before restore, continuing with local state');
          }
        }

        const existingMap = new Map();
        current.items.forEach(it => {
          existingMap.set(`${it.productId}__${JSON.stringify(it.customizations||{})}`, it);
        });

        // Direct replace on server: ensures idempotent accurate restoration
        const payloadItems = cartData.items.map(i => ({
          productId: i.productId,
          quantity: i.quantity,
          customizations: i.customizations || {}
        }));
        try {
          await cartService.replaceCart(payloadItems);
        } catch (e) {
          console.warn('Batch replace failed, falling back to incremental restore', e.message);
          for (const item of cartData.items) {
            try {
              await cartService.updateCart({
                productId: item.productId,
                quantity: item.quantity,
                customizations: item.customizations || {}
              });
            } catch (inner) {
              try {
                await cartService.addToCart({
                  productId: item.productId,
                  quantity: item.quantity,
                  customizations: item.customizations || {}
                });
              } catch (addErr) {
                console.warn('Failed to add during fallback', item.productId, addErr.message);
              }
            }
          }
        }
        await loadCart();
      } else {
        // Guest: direct replace (not additive)
        const guestCart = {
          items: cartData.items.map(i => ({ ...i })),
          total: cartData.total || cartData.items.reduce((t, it) => t + (it.price * it.quantity), 0)
        };
        setCart(guestCart);
        localStorage.setItem('guestCart', JSON.stringify(guestCart));
      }
      toast.success('Cart restored successfully!');
    } catch (error) {
      console.error('Error restoring cart:', error);
      toast.error('Failed to restore cart');
    } finally {
      setLoading(false);
    }
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartItemCount,
    loadCart,
    restoreCart,
    // Utility: attempt restore from last snapshot if present
    restoreFromSnapshot: async () => {
      const raw = sessionStorage.getItem('preClearCartSnapshot');
      if (!raw) return false;
      try {
        const snap = JSON.parse(raw);
        if (snap.items && snap.items.length) {
          await restoreCart({ items: snap.items, total: snap.total });
          return true;
        }
      } catch (e) {
        console.warn('Failed to parse cart snapshot', e);
      }
      return false;
    }
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
